import React, { useEffect, useState } from 'react';
import { Spin, Typography } from 'antd';
import { ArrowRightOutlined, CheckSquareOutlined, InboxOutlined, TruckOutlined } from '@ant-design/icons';
import type { ObjectCardTone } from '../../components/layout/ObjectCard';
import DashboardWidgetHeader from './DashboardWidgetHeader';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { TransportRouteSummaryDto } from '../../dto/transport';
import { SupplierOrderDto } from '../../dto/supplierOrder';
import { listTransportRoutes } from '../transports/api/transportRoutesApi';
import { fetchSupplierOrders } from '../orders/api/supplierOrdersApi';
import { internalTaskApi, InternalTask } from '../../services/internalTaskApi';
import { authService } from '../../services/auth';

const { Text } = Typography;

// Widgets with their own data. Each one fetches only while it is on the panel
// (the page mounts it only when pinned) and again when `refreshKey` changes,
// i.e. with the panel's 60-second refresh. None of them shows money.

interface LiveWidgetProps {
  refreshKey: number;
}

const TRANSPORT_EXCLUDED_STATUSES = ['CANCELLED', 'DELIVERED'];
const SUPPLIER_ORDER_WAITING_STATUSES = ['SENT', 'CONFIRMED', 'IN_PRODUCTION', 'READY', 'SHIPPED', 'PARTIALLY_RECEIVED'];
const UPCOMING_WINDOW_DAYS = 14;
const LIST_LIMIT = 6;

const WidgetFrame: React.FC<{
  title: string;
  icon: React.ReactNode;
  tone: ObjectCardTone;
  link: { to: string; label: string };
  children: React.ReactNode;
}> = ({ title, icon, tone, link, children }) => (
  <section className="dashboard-section">
    <DashboardWidgetHeader
      title={title}
      icon={icon}
      tone={tone}
      aside={<Link to={link.to}>{link.label} <ArrowRightOutlined /></Link>}
    />
    {children}
  </section>
);

const WidgetBody: React.FC<{ loading: boolean; failed: boolean; empty: boolean; children: React.ReactNode }> = ({ loading, failed, empty, children }) => {
  const { t } = useTranslation(['dashboard']);
  if (loading) return <div className="dashboard-list"><Spin size="small" /></div>;
  if (failed) return <div className="dashboard-list"><Text type="danger">{t('dashboard:customize.loadFailed')}</Text></div>;
  if (empty) return <div className="dashboard-list"><Text type="secondary" className="dashboard-margin-empty">{t('dashboard:customize.emptyList')}</Text></div>;
  return <div className="dashboard-list">{children}</div>;
};

export const UpcomingTransportsWidget: React.FC<LiveWidgetProps> = ({ refreshKey }) => {
  const { t } = useTranslation(['dashboard']);
  const [rows, setRows] = useState<TransportRouteSummaryDto[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    listTransportRoutes({
      dateFrom: dayjs().startOf('day').toISOString(),
      dateTo: dayjs().add(UPCOMING_WINDOW_DAYS, 'day').endOf('day').toISOString(),
    })
      .then((list) => {
        if (!active) return;
        setRows(list
          .filter((route) => !TRANSPORT_EXCLUDED_STATUSES.includes(route.status))
          .sort((a, b) => a.departureDate.localeCompare(b.departureDate))
          .slice(0, LIST_LIMIT));
        setFailed(false);
      })
      .catch((error) => {
        console.error('Failed to load upcoming transports:', error);
        if (active) setFailed(true);
      });
    return () => { active = false; };
  }, [refreshKey]);

  return (
    <WidgetFrame title={t('dashboard:customize.widgets.upcomingTransports.label')} icon={<TruckOutlined />} tone="teal" link={{ to: '/app/transports', label: t('dashboard:viewAll') }}>
      <WidgetBody loading={rows === null && !failed} failed={failed && rows === null} empty={rows !== null && rows.length === 0}>
        {(rows || []).map((route) => (
          <div key={route.id} className="dashboard-list-row">
            <span className="dashboard-list-row__main">
              <Link to={`/app/transports/${route.id}`}>{route.displayLabel || route.routeNumber}</Link>
              <Text type="secondary">{route.originCity} → {route.destinationCity}</Text>
            </span>
            <Text strong>{dayjs(route.departureDate).format('DD.MM HH:mm')}</Text>
          </div>
        ))}
      </WidgetBody>
    </WidgetFrame>
  );
};

export const ExpectedDeliveriesWidget: React.FC<LiveWidgetProps> = ({ refreshKey }) => {
  const { t } = useTranslation(['dashboard']);
  const [rows, setRows] = useState<SupplierOrderDto[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    // The list endpoint takes one status per call; overdue and near deliveries
    // are then picked on the client.
    Promise.all(SUPPLIER_ORDER_WAITING_STATUSES.map((status) => fetchSupplierOrders({ status })))
      .then((lists) => {
        if (!active) return;
        const horizon = dayjs().add(UPCOMING_WINDOW_DAYS, 'day');
        const unique = new Map<string, SupplierOrderDto>();
        lists.flat().forEach((order) => unique.set(order.id, order));
        setRows(Array.from(unique.values())
          .filter((order) => order.expectedDelivery && dayjs(order.expectedDelivery).isBefore(horizon))
          .sort((a, b) => (a.expectedDelivery || '').localeCompare(b.expectedDelivery || ''))
          .slice(0, LIST_LIMIT));
        setFailed(false);
      })
      .catch((error) => {
        console.error('Failed to load expected deliveries:', error);
        if (active) setFailed(true);
      });
    return () => { active = false; };
  }, [refreshKey]);

  return (
    <WidgetFrame title={t('dashboard:customize.widgets.expectedDeliveries.label')} icon={<InboxOutlined />} tone="indigo" link={{ to: '/app/supplier-orders', label: t('dashboard:viewAll') }}>
      <WidgetBody loading={rows === null && !failed} failed={failed && rows === null} empty={rows !== null && rows.length === 0}>
        {(rows || []).map((order) => (
          <div key={order.id} className="dashboard-list-row">
            <span className="dashboard-list-row__main">
              <Link to={`/app/supplier-orders/${order.id}`}>{order.orderNumber}</Link>
              <Text type="secondary">{order.supplier?.name || '-'}</Text>
            </span>
            <Text strong>{dayjs(order.expectedDelivery).format('DD.MM')}</Text>
          </div>
        ))}
      </WidgetBody>
    </WidgetFrame>
  );
};

export const MyTasksWidget: React.FC<LiveWidgetProps> = ({ refreshKey }) => {
  const { t } = useTranslation(['dashboard']);
  const [rows, setRows] = useState<InternalTask[] | null>(null);
  const [failed, setFailed] = useState(false);
  const userId = authService.getCurrentUser()?.id;

  useEffect(() => {
    let active = true;
    if (!userId) {
      setRows([]);
      return undefined;
    }
    Promise.all([
      internalTaskApi.getTasks({ dueDate: 'overdue', assigneeId: userId }),
      internalTaskApi.getTasks({ dueDate: 'today', assigneeId: userId }),
    ])
      .then(([overdue, today]) => {
        if (!active) return;
        const unique = new Map<string, InternalTask>();
        [...overdue, ...today].forEach((task) => unique.set(task.id, task));
        setRows(Array.from(unique.values())
          .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
          .slice(0, LIST_LIMIT));
        setFailed(false);
      })
      .catch((error) => {
        console.error('Failed to load my tasks:', error);
        if (active) setFailed(true);
      });
    return () => { active = false; };
  }, [refreshKey, userId]);

  const today = dayjs().startOf('day');
  return (
    <WidgetFrame title={t('dashboard:customize.widgets.myTasks.label')} icon={<CheckSquareOutlined />} tone="green" link={{ to: '/app/tasks', label: t('dashboard:viewAll') }}>
      <WidgetBody loading={rows === null && !failed} failed={failed && rows === null} empty={rows !== null && rows.length === 0}>
        {(rows || []).map((task) => {
          const overdue = task.dueDate ? dayjs(task.dueDate).isBefore(today) : false;
          return (
            <div key={task.id} className="dashboard-list-row">
              <span className="dashboard-list-row__main">
                <Link to="/app/tasks">{task.title}</Link>
                <Text type="secondary">{task.displayOrderNumber || task.customerName || '-'}</Text>
              </span>
              <Text strong type={overdue ? 'danger' : undefined}>
                {task.dueDate ? dayjs(task.dueDate).format('DD.MM') : '-'}
              </Text>
            </div>
          );
        })}
      </WidgetBody>
    </WidgetFrame>
  );
};
